import { Image } from "@/components/common/image";
import macosFolderBlack from '@/assets/agent-user-detail/macos-folder-black@3x.webp';
import { useEffect, useState } from "react";
import { RadioButton } from "../common/radio-button";
import { cn } from "@/lib/utils";
import { MoreActionItem, MoreActionPopover } from "../common/more-action-popover";
import { DownloadIcon, MoreIcon, TrashIcon } from "../ui/icon";
import { useTranslation } from "react-i18next";
import { EditIcon } from "../agent-dashboard/assets";
import { DeleteConfirmModal } from "../ui/modal";
import { formatDateSmart } from "@/utils/date-formatter";
import { TextareaEditable } from "../common/textarea-editable";
import knowledgeBg from '@/assets/assets/knowledge-bg.webp';

interface KnowledgeCardProps {
  id: number;
  name: string;
  time: number;
  /** 是否开启选择 */
  openSelected?: boolean;
  /** 是否选中 */
  isSelected?: boolean;
  isEditName?: boolean;
  /** 选择状态改变回调 */
  onSelected?: (selected: { id: number; checked: boolean }) => void;
  /** 删除回调 */
  onDelete?: (id: number) => void;
  /** 下载回调 */
  onDownload?: (id: number) => void;
  /** 重命名回调 */
  onRename?: (id: number, name: string) => void;
  /** 跳转详情页回调 */
  onDetail?: (id: number) => void;
}

/** 知识库卡片 */
export const KnowledgeCard = ({
  id,
  name,
  time,
  openSelected,
  isSelected,
  isEditName,
  onSelected,
  onDelete,
  onDownload,
  onRename,
  onDetail,
}: KnowledgeCardProps) => {
  const { t } = useTranslation();
  const [checked, setChecked] = useState(isSelected || false);
  // 添加状态来控制TextareaEditable的编辑状态
  const [isEditingName, setIsEditingName] = useState(false);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  useEffect(() => {
    setIsEditingName(isEditName || false);
  }, [isEditName]);

  useEffect(() => {
    setChecked(isSelected || false);
  }, [isSelected]);

  const handleSelect = (value: boolean, id: number) => {
    setChecked(value);
    onSelected?.({ id, checked: value });
  }

  const handleRename = (newName: string) => {
    onRename?.(id, newName);
    // 重命名完成后关闭编辑状态
    setIsEditingName(false);
  };

  // 处理编辑状态变化
  const handleEditingChange = (editing: boolean) => {
    setIsEditingName(editing);
  };

  // 开始重命名操作
  const startRename = () => {
    setIsEditingName(true);
  };

  return (
    <div
      knowledge-card
      className="w-full relative"
      onClick={(e) => e.stopPropagation()}
    >
      <div className={cn(
        "group w-full h-full rounded-[12px] group px-[18px] py-[10px] cursor-pointer transition-colors select-none",
        "hover:bg-[rgba(234,236,240,1)]",
        checked && "bg-[rgba(234,236,240,1)]",
      )}
        onClick={() => {
          if (openSelected) {
            handleSelect(!checked, id);
          }
        }}
      >
        <div className={cn("relative w-full rounded-[12px] transition-all select-none")}>
          <div
            className='relative overflow-hidden flex-shrink-0'
            onClick={(e) => {
              if (openSelected) return;
              e.stopPropagation();
              onDetail?.(id);
            }}>
            <Image src={knowledgeBg} className="size-full object-cover" />
          </div>
          <RadioButton
            checked={checked}
            onChange={(value) => handleSelect(value, id)}
            className={cn(
              "absolute size-6 top-0 -left-[10px] rounded-[5px] border-[3px] border-white bg-[rgba(0,0,0,0.25)]",
              "opacity-0 group-hover:opacity-100",
              checked && "opacity-100 bg-[rgba(90,92,96,1)]",
            )}
          />
        </div>
        {/* 更多操作 */}
        <div
          className="opacity-0 group-hover:opacity-100 absolute top-2.5 right-[10px]"
          onClick={(e) => e.stopPropagation()}
        >
          <MoreActionPopover
            className="w-40"
            side="bottom"
            onOpenChange={setIsPopoverOpen}
            tiggerIcon={
              <MoreIcon className="size-4 text-black" />
            }
            tiggerClassName="hover:bg-white/60 bg-white/60 shadow-sm"
          >
            <MoreActionItem
              icon={<EditIcon className="w-4 h-4" />}
              label={t("common.rename")}
              className='text-[rgba(16,24,40,1)] font-medium'
              onClick={() => startRename()}
            />
            {/* <MoreActionItem
              icon={<DownloadIcon className="w-4 h-4" />}
              label={t("common.download")}
              className='text-[rgba(16,24,40,1)] font-medium'
              onClick={() => onDownload?.(id)}
            /> */}
            <DeleteConfirmModal
              trigger={
                <MoreActionItem
                  icon={<TrashIcon className="w-4 h-4" />}
                  className='text-[rgba(16,24,40,1)] font-medium'
                  label={t("common.delete")}
                />
              }
              onConfirm={() => onDelete?.(id)}
            />
          </MoreActionPopover>
        </div>
      </div>
      <div
        className="w-full absolute -bottom-2 translate-y-full"
        onClick={(e) => e.stopPropagation()}
      >
        <TextareaEditable
          value={name}
          onChange={handleRename}
          maxRows={3}
          textareaClassName="max-h-16"
          textClassName="text-base text-[rgba(16,24,40,1)] font-medium"
          isEditing={isEditingName}
          onEditingChange={handleEditingChange}
          maxLength={20}
        />
        <div className="w-full text-center pt-0.5 text-xs text-[rgba(102,112,133,1)]">
          {formatDateSmart(time * 1000).replace(
            "Today",
            t("creation_folder.project_card.today")
          )}
        </div>
      </div>
    </div>
  )
}
